// @flow
import * as React from "react";
import styled, { ThemeConsumer } from "styled-components";
import { rtl } from "./styled-mixins";

const Container = styled.div`
  direction: ${({ theme }) => theme.direction};
  position: relative;
  display: flex;
  overflow: hidden;
  flex: 1;
  width: 100%;
`;

const Inner = styled.div`
  position: relative;
  overflow-y: scroll;
  overflow-x: hidden;
  flex: 1;
  -webkit-overflow-scrolling: touch;
  
  @media (max-width: 959px) {
    overflow-y: ${({ disableOverflow }) => disableOverflow ? "hidden" : "scroll"};
  }
`;

const Track = styled.div`
  position: absolute;
  width: auto;
  ${rtl("right", 0)};
`;

const Bar = styled.div`
  flex: 1;
  width: 6px;
  margin: 2px;
  background-color: ${({ theme }) => theme.scrollColor};
  opacity: 0.75;
  border-radius: 4px;
`;

let defaultBar = <Bar />;

type Props = {
  className?: string,
  bar?: React.Node,
  style?: { [key: string]: string | number },
  children: React.Node
};

type ScrollableProps = {
  ...Props,
  theme: { direction: "rtl" | "ltr" }
};

type State = {
  nativeScrollbarWidth: number
};

class Scrollable extends React.Component<ScrollableProps, State> {
  state = {
    nativeScrollbarWidth: 17
  };

  containerRef = React.createRef();
  barRef = React.createRef();

  tracking = false;
  top = 0;
  height = 0;

  measure = () => {
    if (this.containerRef.current) {
      if (
        this.containerRef.current.clientHeight ===
        this.containerRef.current.scrollHeight
      ) {
        this.top = 0;
        this.height = 0;
      } else {
        let proportion =
          this.containerRef.current.clientHeight /
          this.containerRef.current.scrollHeight;

        this.top = this.containerRef.current.scrollTop * proportion + "px";
        this.height =
          this.containerRef.current.clientHeight * proportion + "px";
      }
    }
  };

  flush = () => {
    if (this.barRef.current) {
      this.barRef.current.style.top = this.top;
      this.barRef.current.style.height = this.height;
    }
  };

  align = e => {
    this.measure();
    this.flush();
    if (typeof this.props.scrollFunc === "function") {
      this.props.scrollFunc(e);
    }
  };

  track = () => {
    let raf = null;
    if (this.tracking) {
      let prevTop = this.top;
      let prevHeight = this.height;
      this.measure();
      if (this.top !== prevTop || this.height !== prevHeight) {
        this.flush();
      }
      raf = window.requestAnimationFrame && window.requestAnimationFrame(this.track);
    }
    return () => window.cancelAnimationFrame && window.cancelAnimationFrame(raf);
  };

  dragged = false;
  takeoffPageY = 0;
  takeoffScrollTop = 0;

  handleMouseDown = (event: SyntheticMouseEvent<EventTarget>) => {
    event.preventDefault();
    this.dragged = true;
    this.takeoffPageY = event.pageY;
    if (this.containerRef.current) {
      this.takeoffScrollTop = this.containerRef.current.scrollTop;
    }
  };

  handleMouseMove = (event: SyntheticMouseEvent<EventTarget>) => {
    if (this.dragged && this.containerRef.current) {
      let dy = event.pageY - this.takeoffPageY;

      let proportion =
        this.containerRef.current.clientHeight /
        this.containerRef.current.scrollHeight;

      this.containerRef.current.scrollTop =
        this.takeoffScrollTop + dy / proportion;
    }
  };

  handleMouseUp = () => {
    this.dragged = false;
    this.takeoffPageY = 0;
    this.takeoffScrollTop = 0;
  };

  componentDidMount() {
    this.tracking = true;
    this.cancelRaf = this.track();

    document.addEventListener("mousemove", this.handleMouseMove);
    document.addEventListener("mouseup", this.handleMouseUp);

    if (this.containerRef.current) {
      this.setState({
        nativeScrollbarWidth:
          this.containerRef.current.offsetWidth -
          this.containerRef.current.clientWidth
      });
    }
  }

  componentWillUnmount() {
    this.tracking = false;
    this.cancelRaf();
    document.removeEventListener("mousemove", this.handleMouseMove);
    document.removeEventListener("mouseup", this.handleMouseUp);
  }

  render() {
    let { bar = defaultBar, className } = this.props;

    return (
      <Container
        className={className}
        style={{
          ...this.props.style
        }}
      >
        <Inner
          disableOverflow={this.props.disableOverflow}
          style={
            this.props.theme.direction === "rtl"
              ? {
                  left: -this.state.nativeScrollbarWidth,
                  marginRight: -this.state.nativeScrollbarWidth
                }
              : {
                  right: -this.state.nativeScrollbarWidth,
                  marginLeft: -this.state.nativeScrollbarWidth
                }
          }
          ref={this.containerRef}
          onScroll={this.align}
        >
          {this.props.children}
        </Inner>
        <Track
          style={{
            top: this.top,
            display: this.state.nativeScrollbarWidth > 0 ? "flex" : "none",
            height: this.height
          }}
          ref={this.barRef}
          onMouseDown={this.handleMouseDown}
        >
          {bar}
        </Track>
      </Container>
    );
  }
}

export default (props: Props) => (
  <ThemeConsumer>
    {theme => <Scrollable {...props} theme={theme} />}
  </ThemeConsumer>
);



// WEBPACK FOOTER //
// ./src/components/common/Scrollable.js